import get from 'lodash.get';
import React, { useContext, useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { AiFillInfoCircle } from 'react-icons/ai';
import { IoMdCheckmarkCircle } from 'react-icons/io';
import { toast } from 'react-toastify';

import apiUtil from 'src/api/activation';
import CustomToast from 'src/components/CustomToast';
import { AppContext } from 'src/containers/Core/AppContext';
import { AppState, setBusinessOnboardingDetails } from 'src/containers/Core/AppReducer';
import { SUPPORT_ERROR } from 'src/containers/Core/Messages';
import { BUSINESS_ONBOARDING_STEP, OnboardingStepProcess } from '..';

import './style.scss';

type ConflictModalProps = {
  onHide: VoidFunction;
  isManual: boolean;
  conflict: ConflictIdentifierResponse;
};

const ConflictModal: React.FC<ConflictModalProps> = ({ conflict, isManual, onHide }) => {
  if (!conflict) {
    return null;
  }

  const [loading, setLoading] = useState<boolean>(false);
  const [contactSupportLoading, setContactSupportLoading] = useState<boolean>(false);
  const { store, dispatch } = useContext(AppContext);
  const { businessOnboardingDetails }: AppState = store;

  const { entity, askForConfirmation } = conflict;

  const businessUuid = get(businessOnboardingDetails, 'business.uuid');
  const isBlocked = get(businessOnboardingDetails, 'isBlocked');

  const updateOnboardingDetails = () => {
    dispatch(
      setBusinessOnboardingDetails({
        ...businessOnboardingDetails,
        posState: {
          entity: conflict.entity,
          askForConfirmation: false,
        },
        isBlocked: true,
      })
    );
  };

  const onYes = async () => {
    try {
      setLoading(true);
      await apiUtil.acceptDescriptor(businessUuid, entity.uuid, isManual);
      dispatch(
        setBusinessOnboardingDetails({
          ...businessOnboardingDetails,
          posState: {
            entity: conflict.entity,
            askForConfirmation: false,
          },
          isBlocked: false,
          state: OnboardingStepProcess.CL,
          step: BUSINESS_ONBOARDING_STEP.ONBOARD_BUSINESS_COMPLETE,
        })
      );
      onHide();
    } catch (error) {
      toast(<CustomToast description={get(error, 'response.data.error')} />);
    } finally {
      setLoading(false);
    }
  };

  const onContactSupportClick = async () => {
    try {
      setContactSupportLoading(true);
      await apiUtil.rejectDescriptor(businessUuid, entity.uuid, isManual);
      updateOnboardingDetails();
    } catch {
      toast(<CustomToast title="Contact Support" description={SUPPORT_ERROR} />);
    } finally {
      setContactSupportLoading(false);
    }
  };

  const renderIcon = () => {
    if (isBlocked) {
      return <IoMdCheckmarkCircle size={50} className="purple-color" />;
    } else {
      return <AiFillInfoCircle size={50} className="purple-color" />;
    }
  };

  const renderBlockedMessage = () => (
    <div className="font-size-18 m-3 text-center">
      Your request has been sent to contact support. The support team will get in touch with you soon
    </div>
  );

  const renderData = () => (
    <>
      <span className="text-muted w-75 my-3 text-center">
        We have found your information to be match with the following business
      </span>
      <strong className="font-size-16 text-primary">{get(entity, 'name')}</strong>
      <small className="text-muted w-75 text-center">
        {get(entity, ['address', 'address1'])}, {get(entity, ['address', 'city'])}
      </small>
      {askForConfirmation ? (
        <div className="d-flex flex-column align-items-center justify-content-center">
          <span className="text-muted mt-3 text-center">Is this your business profile?</span>
          <div className="d-flex flex-column">
            <Button className="mt-3" disabled={loading || contactSupportLoading} onClick={onYes}>
              {loading ? <Spinner animation="border" size="sm" className="mx-3" /> : 'Yes'}
            </Button>
            <Button
              variant="outline-secondary"
              disabled={loading || contactSupportLoading}
              className="mt-3"
              onClick={onContactSupportClick}
            >
              {contactSupportLoading ? (
                <Spinner animation="border" size="sm" className="mx-3" />
              ) : (
                'No, contact support'
              )}
            </Button>
          </div>
        </div>
      ) : (
        <div className="d-flex flex-column align-items-center justify-content-center">
          <span className="text-danger text-center mt-3">
            This business already has an existing account on Open Rewards. Someone from our support will contact you
            soon.
          </span>
          <Button className="mt-3" onClick={updateOnboardingDetails}>
            Ok, Understood
          </Button>
        </div>
      )}
    </>
  );

  return (
    <Modal show={true} onHide={onHide} backdrop="static">
      <Modal.Body>
        <div className="d-flex flex-column align-items-center justify-content-center p-3">
          {renderIcon()}
          {isBlocked ? renderBlockedMessage() : renderData()}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ConflictModal;
